import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useEffectOnce } from 'react-use';

import { Location } from 'history';
import _ from 'lodash';
import moment from 'moment';
import queryString from 'query-string';
import {
  Message,
  PatientForm,
  PatientFormTemplate,
  useMessageCreateOne,
  usePatientFormCreateOne,
  usePatientFormUpdateById,
  usePatientLogin,
  usePatientVerify,
} from 'requests/resources';
import { v4 as uuid } from 'uuid';

import { changeLocale } from '@lib/actions';
import { normalizeLocale } from '@lib/actions/changeLocale';
import { useCurrentLanguage, useCurrentUser } from '@lib/hooks';
import { patientFormsResource } from '@lib/resources';
import {
  PatientFormStatus,
  PatientFormTemplateItem,
  PatientFormTemplateItemRedirectType,
  PatientFormTemplateLumaBotFlow,
  UserBase,
} from '@lib/resources/types';
import configService from '@lib/services/configService';
import { getLumaBotNotifyStaffMessageText } from '@lib/services/lumaBotService';
import { addQuery } from '@lib/services/queryService';
import storageService from '@lib/services/storageService';
import axiosClient from '@lib/utils/axiosClient';

import { generateMessageTemplateKey, useSocketEventsDebounced } from 'hooks';
import { STORAGE_KEYS } from 'utils/constants';

import { TMessageWrapper } from '../../containers/Messages/MessageWrapper';
import { chatContextDefaultValue, ChatContextProps } from '../../contexts/ChatContext';
import useSettingsLumaBot from '../useSettingsLumaBot';

import { LumaBotAnswers } from './interfaces';
import {
  getLumaBotFlow,
  getLumaBotFlowNextItem,
  getLumaBotFlowUpdated,
  getLumaBotPatientData,
  sanitizeLumaBotMessage,
} from './utils';

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const _getAwaitingTime = (prevMessage?: Partial<TMessageWrapper>): number => {
  const millisecondsMultiplier = 20;
  const currentDate = new Date();
  const maxMilliseconds = 1500;
  const minMilliseconds = 800;

  const prevMessageText = prevMessage?.text;
  const prevMessageCreatedAt = prevMessage?.createdAt;
  const isPrevInbound = prevMessage?.type !== 'outbound';
  // @ts-ignore
  const howLongMessageGotCreated = currentDate - new Date(prevMessageCreatedAt);

  if (isPrevInbound && prevMessageText) {
    const expectedAwaitingTime = prevMessageText.length * millisecondsMultiplier;
    const shouldAddAwaitingTime = howLongMessageGotCreated < expectedAwaitingTime;
    const timingDifference = expectedAwaitingTime - howLongMessageGotCreated;
    const howManyTimeItShouldAdd = timingDifference < maxMilliseconds ? timingDifference : maxMilliseconds;

    return shouldAddAwaitingTime ? howManyTimeItShouldAdd : 0;
  }

  const minimumTimeToAdd = howLongMessageGotCreated > minMilliseconds ? 0 : minMilliseconds - howLongMessageGotCreated;
  return prevMessageText ? minimumTimeToAdd : 0;
};

/**
 * Handle messages and updates for LumaBot
 *
 * TODO: Most of this logic should go to BE side so we gain on performance and we can get rid of mixed content
 */
export default function useLumaBotActions(
  flowTemplates?: PatientFormTemplate[],
  flowId?: string,
  variant?: ChatContextProps['variant'],
  isPopper?: boolean,
): ChatContextProps {
  const [state, setState] = useState<Omit<ChatContextProps, 'messages'> & { messages: Partial<TMessageWrapper>[] }>(
    chatContextDefaultValue,
  );
  const dispatch = useDispatch();
  const currentUser = useCurrentUser() as UserBase;
  const language = useCurrentLanguage();
  const settings = useSettingsLumaBot();
  const inputRef = useRef<HTMLElement | null>(null);

  // Templates and flows the patient has gone through
  const flowTemplatesRef = useRef<PatientFormTemplate[]>([]);
  const flowsRef = useRef<PatientForm[]>([]);

  // The last item being displayed
  const currentItemRef = useRef<PatientFormTemplateItem>();

  // Answers (used for replacements and patient creation)
  const answers = useRef<LumaBotAnswers>({});

  // To show/hide the typing indicator during live chat
  const timerTypingRef = useRef(0);

  // mutations
  const patientFormCreate = usePatientFormCreateOne();
  const patientFormUpdate = usePatientFormUpdateById();
  const patientLogin = usePatientLogin();
  const patientVerify = usePatientVerify();
  const messageCreateOne = useMessageCreateOne();

  // Set initial locale from user's language
  useEffectOnce(() => {
    dispatch(changeLocale(normalizeLocale(language)));
  });

  // Handle messages for live chat
  useSocketEventsDebounced(['chat:server:outbound:message'], (array: any[]) => {
    if (!settings?.liveChat?.enabled) return;

    _.forEach(array, (data?: Message) => {
      const text = data?.text;
      const automated = data?.automated;
      const user = data?.user;
      if (text && !automated) {
        setState((prevState) => ({ ...prevState, isLiveChatEnabled: true, typing: false }));
        setTimeout(() => _pushMessage({ text, user, type: 'inbound' }, { skipNextItem: true }));
      }
    });
  });

  // Handle typing indicator for live chat
  useSocketEventsDebounced(['chat:server:typing:start'], (values) => {
    if (!settings?.liveChat?.enabled) return;

    setState((prevState) => ({ ...prevState, typing: true }));
    clearTimeout(timerTypingRef.current);

    // Hide indicator after $delay not typing
    timerTypingRef.current = window.setTimeout(() => {
      setState((prevState) => ({ ...prevState, typing: false }));
    }, 3000);
  });

  // useMemo so we can update texts on real-time when the language changes
  // TODO: Skip sanitizing the entire list of messages each time there's a change
  const messagesSanitized = useMemo(() => {
    const result: TMessageWrapper[] = _.map(state.messages, (message, index, collection) => {
      const nextMessage = collection[index + 1];
      const prevMessage = collection[index - 1];

      return {
        ...sanitizeLumaBotMessage(message, language, settings.name || '', answers.current),
        metadata: {
          ...message.metadata,
          isLastMessage: index === state.messages.length - 1,
          nextMessage:
            nextMessage && sanitizeLumaBotMessage(nextMessage, language, settings.name || '', answers.current),
          prevMessage:
            prevMessage && sanitizeLumaBotMessage(prevMessage, language, settings.name || '', answers.current),
        },
      };
    });
    // Return button should only be available for the last outbound message
    const lastOutboundMsg = _.findLast(result, { type: 'outbound' });
    const lastInboundMsg = _.findLast(result, { type: 'inbound' });

    const shouldRevertBeDisabled = state.loading || state.typing || (lastInboundMsg && lastInboundMsg.user);
    if (lastOutboundMsg && !_.has(lastOutboundMsg.metadata, 'includeRevert') && !shouldRevertBeDisabled) {
      lastOutboundMsg.metadata.includeRevert = true;
    }

    return result;
  }, [state.messages, language, settings, state.loading, state.typing]);

  const _focusInput = () => inputRef.current?.focus();

  /**
   * Push flow template
   *
   * E.g.: To push `nextPatientForm` for linked forms, or force welcome/speakWithStaff core flows
   */
  const _pushFlowTemplate = useCallback(
    async (flowTemplate?: PatientFormTemplate, config?: { skipPushingItem?: boolean }) => {
      if (!flowTemplate) return;

      // Diable live chat mode
      setState((prevState) => ({ ...prevState, isLiveChatEnabled: false }));

      // Update flowTemplate URL parameter for tracking purposes
      addQuery({ flowTemplate: flowTemplate._id });

      // Push the new flow
      const lastFlow = _.last(flowsRef.current);
      const newFlow = {
        ...getLumaBotFlow(currentUser, flowTemplate),
        ...(lastFlow && { previousPatientForm: lastFlow._id }),
      };
      flowTemplatesRef.current.push(flowTemplate);
      flowsRef.current.push(newFlow as PatientForm);

      // Set the previous flow as completed
      if (lastFlow) await patientFormUpdate.mutateAsync({ ...lastFlow, status: PatientFormStatus.Completed });

      if (!config || !config.skipPushingItem) {
        // Render the first item available in the flow
        const nextItem = getLumaBotFlowNextItem(flowTemplate);
        if (nextItem) pushItem(nextItem);
      }
    },
    // @ts-ignore
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    [patientFormUpdate, currentUser, pushItem],
  );

  const _getLastFlowTemplate = useCallback(
    () =>
      _.findLast(
        flowTemplatesRef.current,
        ({ lumaBotFlow }) =>
          lumaBotFlow !== PatientFormTemplateLumaBotFlow.KnockKnock &&
          lumaBotFlow !== PatientFormTemplateLumaBotFlow.SessionTimeout,
        flowTemplatesRef.current.length - 2,
      ),
    [],
  );

  const _getLastFlowTemplateItem = useCallback(
    (lastFlowTemplate: PatientFormTemplate) => {
      const lastMessage =
        lastFlowTemplate && _.findLast(state.messages, (m) => m.metadata?.flowTemplateId === lastFlowTemplate._id);
      return lastMessage?.metadata?.item;
    },
    [state.messages],
  );

  const _goToForm = useCallback((_id: string) => _pushFlowTemplate(_.find(flowTemplates, { _id })), [
    flowTemplates,
    _pushFlowTemplate,
  ]);

  const _goToPrevious = useCallback(async () => {
    const lastFlowTemplate = _getLastFlowTemplate();
    const previousFlowTemplateItem = lastFlowTemplate && _getLastFlowTemplateItem(lastFlowTemplate);
    if (previousFlowTemplateItem) {
      // Continue from the latest item of the previous flow
      await _pushFlowTemplate(lastFlowTemplate, { skipPushingItem: true });
      pushItem(previousFlowTemplateItem);
    } else {
      // Default to the welcome item
      return _pushFlowTemplate(
        _.find(flowTemplates, flowId ? { _id: flowId } : { lumaBotFlow: PatientFormTemplateLumaBotFlow.Welcome }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_getLastFlowTemplate, _getLastFlowTemplateItem, _pushFlowTemplate, flowTemplates]);

  /**
   * Push flow template item (inbound message)
   */
  const pushItem: (item: PatientFormTemplateItem) => Promise<void> = useCallback(
    async (item) => {
      if (variant === 'backend') {
        // eslint-disable-next-line no-console
        console.log(`variant='backend', pushItem:`, item);
      }
      // Update item URL parameter for tracking purposes
      addQuery({ item: item.id });

      let nextItem;
      const itemType = item.type;

      // Skip 2FA if the patient is already authenticated
      if (itemType === '2FA' && !currentUser.$isWidgetUser) {
        nextItem = getLumaBotFlowNextItem(_.last(flowTemplatesRef.current), item);
        if (nextItem) return pushItem(nextItem);
      }

      let delay = 0;

      // Show `typing`
      setState((prevState) => {
        // FIXME: Skip modifying external vars inside `setState`
        delay = _getAwaitingTime(prevState.messages[prevState.messages.length - 1]);

        // NOTE: `_getAwaitingTime` might return `delay: 0` but we still need to set the `typing: true`
        // due to some messages relying on the delay from the BE to respond
        return { ...prevState, typing: true };
      });
      if (item.type !== 'jump' && delay > 0) await sleep(delay);

      // Update the flow with the new item
      const currentFlow = _.last(flowsRef.current);
      if (currentFlow) {
        let flow;
        if (currentFlow._id) {
          flow = await patientFormUpdate.mutateAsync({
            ...currentFlow,
            responses: {
              ...currentFlow.responses,
              currentItemId: item.id,
              path: _.compact(_.concat(currentFlow.responses.path, item.id)),
            },
          });
        } else {
          flow = await patientFormCreate.mutateAsync(currentFlow);

          // Update `nextPatientForm` in the previous flow
          const lastFlow = _.nth(flowsRef.current, -2);
          if (lastFlow) await patientFormUpdate.mutateAsync({ ...lastFlow, nextPatientForm: flow._id });
        }
        flowsRef.current[flowsRef.current.length - 1] = flow;
      }
      const currentFlowTemplateId = _.get(_.last(flowTemplatesRef.current), '_id');
      const currentFlowId = _.get(_.last(flowsRef.current), '_id');

      // Push new item's message
      if (item.title || (itemType === 'template' && item?.properties?.template)) {
        setState((prevState) => ({
          ...prevState,
          loading: false,
          typing: false,
          currentItem: item,
          messages: _.concat(prevState.messages, {
            _id: uuid(),
            text: item.title,
            type: 'inbound',
            createdAt: moment().format(),
            metadata: {
              item,
              flowTemplateId: currentFlowTemplateId,
              flowId: currentFlowId,
            },
          }),
        }));
      }

      // push first nested item in case of grouped questions
      nextItem = _.get(item, 'properties.items[0]');
      if (nextItem) await pushItem(nextItem);

      // Handle template items as an special case since they might not be final
      // TODO: Unify the logic with answer redirects (note that all this will go to BE side)
      if (item.type === 'template' || item.type === 'book_status_component') {
        // Notify staff
        if (item.properties.notifyStaff) {
          await messageCreateOne.mutateAsync({
            text: getLumaBotNotifyStaffMessageText(_.last(flowsRef.current)!._id, item.id),
          });
        }
        // Handle redirect
        if (item.redirectType === 'goToForm') {
          await _goToForm(_.get(item, 'properties.goToForm'));
        }
        if (item.redirectType === 'goToPrevious') {
          await _goToPrevious();
        }
        if (item.redirectType === 'isFinal') {
          await _pushFlowTemplate(_.find(flowTemplates, { lumaBotFlow: PatientFormTemplateLumaBotFlow.CulDeSac }));
        }
        // Next item
        nextItem = getLumaBotFlowNextItem(_.last(flowTemplatesRef.current), item);
        if (nextItem) await pushItem(nextItem);
      }

      // Execute script with jumping logic in case of a `jump` item
      if (item.type === 'jump') {
        // TODO: Move mutation to react-query
        let answer;
        try {
          const url = `${patientFormsResource.url}/${_.last(flowsRef.current)!._id}/script/${item.id}`;
          const response = await axiosClient.post(url);
          answer = _.find(item.properties.answers, { label: response?.data?.output });
        } catch (e) {
          // ignore scripts failing
        }
        nextItem = getLumaBotFlowNextItem(_.last(flowTemplatesRef.current), item, answer);
        if (nextItem) await pushItem(nextItem);
      }
      if (!nextItem) _focusInput();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      currentUser,
      patientFormCreate,
      patientFormUpdate,
      flowTemplates,
      variant,
      _goToForm,
      _goToPrevious,
      _pushFlowTemplate,
    ],
  );

  /**
   * Push message
   *
   * Used for patient messages (outbound), but it can also be used to push additional inbound messages
   * that are added after the initial flow template item
   *
   * E.g.: validation messages (wrong email, ...), or 2FA messages (code sent, ...)
   */
  const _pushMessage: ChatContextProps['pushAnswer'] = useCallback(
    async (message, opts = { customDelay: 0, skipNextItem: false }) => {
      const metadata = message.metadata as TMessageWrapper['metadata'] | undefined;
      const isInbound = message.type !== 'outbound';
      const itemType = metadata?.item?.type;
      setState((prevState) => ({ ...prevState, loading: true, typing: isInbound }));

      const delay = _getAwaitingTime(state.messages[state.messages.length - 1]);
      if (isInbound && (opts.customDelay || delay)) await sleep(opts.customDelay || delay);

      // Push the message
      if (message.text || itemType === 'book_status_component') {
        const newMessage = { ...message, _id: uuid(), createdAt: moment().format() };
        if (newMessage.type === 'outbound' && metadata?.patient) {
          _.set(newMessage, 'metadata.includeRevert', false);
        }
        if (metadata?.item) {
          currentItemRef.current = metadata.item;
        }
        setState((prevState) => ({
          ...prevState,
          typing: false,
          messages: _.concat(prevState.messages, newMessage),
        }));

        // Submit outbound message on live chat mode
        if (state.isLiveChatEnabled && newMessage.type === 'outbound') {
          await messageCreateOne.mutateAsync(_.pick(message, ['text']));
        }
      }

      // Notify staff
      if (
        // WORKAROUND: `book_status_component` is an inbound rendered by the scheduler item itself
        (message.type === 'outbound' || itemType === 'book_status_component') &&
        metadata?.item?.properties.notifyStaff
      ) {
        await messageCreateOne.mutateAsync({
          text: getLumaBotNotifyStaffMessageText(_.last(flowsRef.current)!._id, metadata.item.id),
        });
      }

      // We're not interested on saving changes nor showing another message
      if (opts.skipNextItem || state.isLiveChatEnabled) {
        _focusInput();
        return setState((prevState) => ({ ...prevState, loading: false }));
      }

      // Update answers
      const squiggly = metadata?.item?.squiggly;
      if (squiggly) {
        const value = metadata?.answer?.label || metadata?.answer || message.text;
        answers.current[squiggly] = value;
      }

      // Update flow with the answer
      const nextItem = getLumaBotFlowNextItem(
        _.last(flowTemplatesRef.current),
        currentItemRef.current,
        metadata?.answer,
      );
      flowsRef.current[flowsRef.current.length - 1] = await patientFormUpdate.mutateAsync(
        getLumaBotFlowUpdated(currentUser, _.last(flowsRef.current)!, currentItemRef.current!, message, nextItem),
      );

      // Message requires sending the 2FA code to the patient
      if (metadata?.action === 'send2FACode') {
        const data = getLumaBotPatientData(answers.current, language);
        try {
          const patient = await patientLogin.mutateAsync(data);
          await _pushMessage(
            {
              text: generateMessageTemplateKey('patientLumaBot2FASendCodeSuccess'),
              metadata: { patient, forceInput: true },
              type: 'inbound',
            },
            { skipNextItem: true },
          );
          _focusInput();
          return;
        } catch (error) {
          return _pushMessage(
            { text: generateMessageTemplateKey('patientLumaBot2FASendCodeError'), type: 'inbound' },
            { skipNextItem: true },
          );
        }
      }
      // Verify patient with the 2FA code
      if (message.type === 'outbound' && metadata?.patient) {
        try {
          await patientVerify.mutateAsync({
            patientId: metadata.patient!,
            code: message.text!,
            location: ({ pathname: '/lumabot' } as unknown) as Location,
          });
          return _pushMessage({
            text: generateMessageTemplateKey('patientLumaBot2FAVerifyCodeSuccess'),
            metadata: { patient: metadata.patient },
            type: 'inbound',
          });
        } catch (error) {
          return _pushMessage(
            {
              text: generateMessageTemplateKey('patientLumaBot2FAVerifyCodeError'),
              metadata: { ...metadata },
              type: 'inbound',
            },
            { skipNextItem: true },
          );
        }
      }

      // Handle redirects
      const redirectType: PatientFormTemplateItemRedirectType =
        metadata?.answer?.redirectType || metadata?.item?.redirectType;
      if (redirectType === 'goToForm') {
        return _goToForm(metadata?.answer?.properties?.goToForm);
      }
      if (redirectType === 'goToPrevious') {
        return _goToPrevious();
      }

      if (redirectType === 'reschedule') {
        const { showFilters, params: propertiesParams } = metadata?.answer?.properties || metadata?.item?.properties;

        storageService.extendParameters({
          ..._.mapValues(propertiesParams, (v) => v.join(',')),
          patientFormTemplate: metadata?.flowTemplateId,
          hideCalendar: !!_.get(propertiesParams, 'hideCalendar', false),
          showFilters,
        });

        // Availabilities / Book screens uses the url parameters to control the filters.
        // Since we need a safe place to get them from, I'm saving the params on the localStorage
        storageService.set(STORAGE_KEYS.patientFormPSFilters, {
          ...propertiesParams,
          patientFormTemplate: metadata?.flowTemplateId,
        });

        const { user, ...storageParams } = storageService.getParameters();

        const webSchedulerUrl = `${configService.getSitePatient()}/availabilities?${queryString.stringify({
          ...storageParams,
          token: storageService.getToken(),
        })}`;

        const searchParams = new URLSearchParams(window.location.search);
        const { widget } = Object.fromEntries(searchParams.entries());

        if (window.parent && widget === 'popper') {
          const action = 'openUrl';
          window.parent.postMessage({ caller: 'lh:lumabot', action, value: webSchedulerUrl }, '*');
        } else {
          window.open(webSchedulerUrl, '_blank');
        }
      }
      if (redirectType === 'externalUrl') {
        const searchParams = new URLSearchParams(window.location.search);
        const { widget } = Object.fromEntries(searchParams.entries());
        const url = metadata?.answer?.properties?.url;

        if (window.parent && widget === 'popper') {
          const action = 'openUrl';
          window.parent.postMessage({ caller: 'lh:lumabot', action, value: url }, '*');
        } else {
          window.open(url, '_blank');
        }
      }

      // Handle completed flow
      if (_.last(flowsRef.current)!.status === PatientFormStatus.Completed) {
        return _pushFlowTemplate(_.find(flowTemplates, { lumaBotFlow: PatientFormTemplateLumaBotFlow.CulDeSac }));
      }

      // Render the next item
      if (nextItem) pushItem(nextItem);
      else {
        setState((prevState) => ({ ...prevState, loading: false }));
        _focusInput();
      }
    },
    [
      _pushFlowTemplate,
      _goToForm,
      _goToPrevious,
      currentUser,
      flowTemplates,
      language,
      patientFormUpdate,
      patientLogin,
      patientVerify,
      messageCreateOne,
      pushItem,
      state.messages,
      state.isLiveChatEnabled,
    ],
  );

  /**
   * This is a wrapper for `_pushMessage` in case we pass the `variant='backend'`
   * so we can test the new BE endpoint in order to store answers
   */
  const pushAnswer: ChatContextProps['pushAnswer'] = useCallback(
    async (message, opts) => {
      if (variant === 'backend') {
        // Render message with the answer
        _pushMessage(message, { ...opts, skipNextItem: true });

        // Delegate logic to BE to store the answer, run jumping logic, and return the next item/s
        setState((prevState) => ({ ...prevState, loading: true, typing: true }));
        const url = `/flows/${message.metadata?.flowId}/items/${message.metadata?.item?.id}/answer`;
        const data = {
          answerId: message.metadata?.answer?.id,
          answerValue: message.metadata?.answer?.label || message.metadata?.answer || message.text,
        };
        // eslint-disable-next-line no-console
        console.log(`variant='backend', POST ${url}`, { data });
        try {
          const response = await axiosClient.post(url, data);
          const items: PatientFormTemplateItem[] = response?.data;
          // eslint-disable-next-line no-console
          console.log(`variant='backend', items:`, items);
          items.forEach(async (item) => {
            await pushItem(item);
          });
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(`variant='backend', Couldn't use BE, falling back to FE logic:`, e);
          _pushMessage(message, opts);
        }
      } else {
        _pushMessage(message, opts);
      }
    },
    [variant, _pushMessage, pushItem],
  );

  /**
   * Move back to a specific message
   */
  const revertMessage: ChatContextProps['revertMessage'] = ({ _id, metadata }) => {
    if (state.loading || state.typing) return;

    // Revert item
    currentItemRef.current = metadata.item;

    // Revert flow
    const currentFlow = _.findLast(flowsRef.current, { _id: metadata.flowId });
    if (currentFlow) {
      currentFlow.status = PatientFormStatus.Started;
      flowsRef.current = _.slice(flowsRef.current, 0, _.findIndex(flowsRef.current, { _id: currentFlow._id }) + 1);
    }

    // Revert flowTemplate
    const end = _.findIndex(flowTemplatesRef.current, { _id: metadata.flowTemplateId }) + 1;
    flowTemplatesRef.current = _.slice(flowTemplatesRef.current, 0, end);

    // Point to the reverted inbound message
    setState((prevState) => ({
      ...prevState,
      messages: _.slice(prevState.messages, 0, _.findIndex(prevState.messages, { _id })),
    }));
    setImmediate(() => _focusInput());
  };

  /**
   * Wrapper to expose the `_pushFlowTemplate` private method
   */
  const pushFlowTemplate: ChatContextProps['pushFlowTemplate'] = useCallback(
    async (lumaBotFlow, text) => {
      let flowTemplate = _.find(flowTemplates, { lumaBotFlow });

      if (lumaBotFlow === 'welcome' && flowId) {
        // Restart from the first flow the user accessed thru the url instead of the default Welcome
        flowTemplate = _.find(flowTemplates, { _id: flowId as string });
      }

      // Skip the flow in case there's no template or we're repeating the knock-knock
      if (
        !flowTemplate ||
        (lumaBotFlow === PatientFormTemplateLumaBotFlow.KnockKnock &&
          lumaBotFlow === _.get(_.last(flowTemplatesRef.current), 'lumaBotFlow'))
      ) {
        return;
      }

      if (text) {
        await _pushMessage({ text, type: 'outbound', metadata: { includeRevert: false } }, { skipNextItem: true });
      }
      await _pushFlowTemplate(flowTemplate);
    },
    [flowTemplates, _pushMessage, _pushFlowTemplate, flowId],
  );

  /**
   * Clear the state. E.g.: when the session expires
   */
  const clear = () => {
    flowTemplatesRef.current = [];
    flowsRef.current = [];
    currentItemRef.current = undefined;
    setState(chatContextDefaultValue);
  };

  // Initialize the chat
  useEffect(() => {
    if (flowTemplates && !_.last(flowTemplatesRef.current)) {
      const flow = _.find(
        flowTemplates,
        flowId ? { _id: flowId } : { lumaBotFlow: PatientFormTemplateLumaBotFlow.Welcome },
      );
      _pushFlowTemplate(flow);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowTemplates]);

  return {
    ...state,
    inputRef,
    settings,
    flowTemplates,
    flowId,
    variant,
    messages: messagesSanitized,
    currentFlowTemplate: _.last(flowTemplatesRef.current),
    currentFlow: _.last(flowsRef.current),
    currentItem: currentItemRef.current,
    isPopper,
    setMode: (newMode) => setState((prevState) => ({ ...prevState, mode: newMode })),
    pushFlowTemplate,
    pushAnswer,
    revertMessage,
    clear,
  };
}
